import React, { useState, useEffect } from "react";
import { Box, useTheme, InputBase, IconButton, TextField } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import SearchIcon from "@mui/icons-material/Search";
import { getHistorialChapa } from "../../services/salaLimpia.services";
import MenuIcon from "@mui/icons-material/Menu";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import QRCode from "qrcode";
import dayjs from "dayjs";
import ModalPreview from "../../modal/modalPreview"; // Importar el modal de vista previa
import ModalChapas from "../../modal/ordenProduccion/modalChapas";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, ImageRun } from "docx";
import Button from "@mui/material/Button";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


const HistorialChapa = () => {
  const [data, setData] = useState([]); // Datos de OrdenProduccion
  const [searchValue, setSearchValue] = useState(""); // Valor del campo de búsqueda
  const [filteredData, setFilteredData] = useState([]); // Datos filtrados para la tabla
  const [open, setOpen] = useState(false);
  const [registro, setRegistro] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [previewData, setPreviewData] = useState(""); // Estado para guardar los datos de vista previa
  const getDefaultStart = () => {
    const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  };
  const getDefaultEnd = () => new Date().toISOString().slice(0, 16);

  const [startDate, setStartDate] = useState(getDefaultStart);
  const [endDate, setEndDate] = useState(getDefaultEnd);
  const [classificationFilter, setClassificationFilter] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const classificationOptions = ["TIPO A", "TIPO B", "TIPO C"];

  const fetchPrueba = async (start, end) => {
    try {
      const response = await getHistorialChapa(start, end);
      console.log("🚀 ~ fetchPrueba ~ response:", response)
      setData(response);
      setFilteredData(response);
    } catch (error) {
      console.error("Error al obtener los datos del backend:", error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPrueba(startDate, endDate);
    }, 500);
    return () => clearTimeout(timer);
  }, [startDate, endDate]);

  useEffect(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    const nextData = data.filter((item) => {
      const matchesSearch = normalizedSearch
        ? String(item?.serie ?? "").toLowerCase().includes(normalizedSearch) ||
          String(item?.cod ?? "").toLowerCase().includes(normalizedSearch)
        : true;

      if (!matchesSearch) return false;

      const matchesClassification = classificationFilter.length
        ? classificationFilter.includes(item?.clasificacion ?? "")
        : true;

      return matchesClassification;
    });

    setFilteredData(nextData);
  }, [data, searchValue, classificationFilter]);

  useEffect(() => {
    const validIds = new Set(
      Object.values(filteredData ?? [])
        .flat()
        .map((row) => String(row.id))
    );
    setSelectionModel((prev) => {
      const next = prev.filter((id) => validIds.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredData]);

  const handleRowClick = (params) => {
    setRegistro(params.row); // Guarda el registro seleccionado
    setOpen(true); // Abre el modal
  };

  const handleCloseModal = () => {
    setOpen(false);
    fetchPrueba(startDate, endDate);
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleClosePreview = () => {
    setPreviewData(""); // Limpiar los datos de vista previa al cerrar el modal
  };

  const formatDate = (date) => {
    if (!date) return '';

    const parsedDate = new Date(date);
    if (isNaN(parsedDate)) return '';

    const day = String(parsedDate.getDate()).padStart(2, '0');
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const year = String(parsedDate.getFullYear()).slice(-2);
    const hours = String(parsedDate.getHours()).padStart(2, '0');
    const minutes = String(parsedDate.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

const flattenFilteredRows = () => Object.values(filteredData ?? []).flat();

const getRowsToExport = () => {
  const selectedIds = new Set(selectionModel.map((id) => String(id)));
  if (!selectedIds.size) {
    return [];
  }

  return flattenFilteredRows().filter((row) => selectedIds.has(String(row.id)));
};

const handleExportExcel = () => {
  const rowsToExport = getRowsToExport();

  if (!rowsToExport.length) {
    toast.info("Selecciona al menos una chapa", { toastId: "no-rows-export" });
    return;
  }

  const exportData = rowsToExport.map((row) => ({
    ID: row.id,
    Orden: row.orden,
    Clasificacion: row.clasificacion,
    Serie: row.serie,
    Producto: row.cod,
    Chapa1: row.fabrica1,
    NotaFiscal1: row.nfChapa1,
    Chapa2: row.fabrica2,
    NotaFiscal2: row.nfChapa2,
    PVB: row.PVB,
    "Calandra Temp": row.temperatura,
    "Hora Proceso": formatDate(row.HoraCalandra),
    "SL Temperatura": row.TemperaturaSalaLimpia,
    "SL Humedad": row.HumedadSalaLimpia,
    "Hora SL": formatDate(row.HoraSalaLimpia),
    Receta: row.recetaAutoClave,
    Id_produccion: row.id_produccion,
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Historial");
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const excelBlob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });
  saveAs(excelBlob, "HistorialChapas.xlsx");
};

const handleExportWord = async () => {
  const rowsToExport = getRowsToExport();

  if (!rowsToExport.length) {
    toast.info("Selecciona al menos una chapa", { toastId: "no-rows-export" });
    return;
  }

  const exportData = rowsToExport.map((row) => ({
    ID: row.id,
    Orden: row.orden,
    Clasificacion: row.clasificacion,
    Serie: row.serie,
    Producto: row.cod,
    Chapa1: row.fabrica1,
    nf1: row.nfChapa1,
    nf2: row.nfChapa2,
    Chapa2: row.fabrica2,
    PVB: row.PVB,
    CalandraTemp: row.temperatura,
    HoraProceso: formatDate(row.HoraCalandra),
    SLTemp: row.TemperaturaSalaLimpia,
    SLHumedad: row.HumedadSalaLimpia,
    HoraSL: formatDate(row.HoraSalaLimpia),
    Receta: row.recetaAutoClave,
    id_produccion: row.id_produccion,
  }));

  // Cargar la imagen como ArrayBuffer
  const imageResponse = await fetch(`${process.env.PUBLIC_URL}/assets/dracena.png`);
  const imageBuffer = await imageResponse.arrayBuffer();

  const doc = new Document({
    sections: [
      {
        children: [
          // Imagen centrada
          new Paragraph({
            children: [
              new TextRun({ text: "", break: 1 }), // Salto para dejar espacio
            ],
            alignment: "center",
          }),
          new Paragraph({
            alignment: "center",
            children: [
             new ImageRun({
              data: imageBuffer,
              transformation: {
                width: 280,
                height: 120,
              },
            })
            ],
          }),

          // Agregamos los datos de cada fila con saltos
          ...exportData.flatMap((item) => [
            new Paragraph({
              children: [
                new TextRun({ text: `ID: ${item.ID} | Orden: ${item.Orden} | Serie: ${item.Serie} | Producto: ${item.Producto}`, bold: true }),
              ],
            }),
            // new Paragraph(`Producto: ${item.Producto}`),
            new Paragraph({
              children: [
                new TextRun({ text: `PAQUETES`, bold: true }),
              ],
            }),
            new Paragraph(`Chapa1: ${item.Chapa1} | NotaFiscal: ${item.nf1} | Chapa2: ${item.Chapa2} | NotaFiscal: ${item.nf2} | PVB: ${item.PVB} ` ),
            
            new Paragraph({
              children: [
                new TextRun({ text: `SALA LIMPIA`, bold: true }),
              ],
            }),
            new Paragraph(`Temperatura: ${item.SLTemp} | Humedad: ${item.SLHumedad} | Fecha: ${item.HoraSL}`),
            
            new Paragraph({
              children: [
                new TextRun({ text: `CALANDRA`, bold: true }),
              ],
            }),
            new Paragraph(`Temp: ${item.CalandraTemp} | Fecha: ${item.HoraProceso} | Clasificacion: ${item.Clasificacion}` ),
           
            new Paragraph({
              children: [
                new TextRun({ text: `AUTOCLAVE`, bold: true }),
              ],
            }),
            new Paragraph(`Receta: ${item.Receta}`),
            new Paragraph("------------------------------------------------------------------------------------------------------------------------------------------"),
            new Paragraph(""), // Espacio extra entre registros
          ]),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
let fileName = "HistorialChapas.docx";

if (exportData.length === 1) {
  const serie = exportData[0].Serie || "Chapa";
  fileName = `ChapaSerie_${serie}.docx`;
}

saveAs(blob, fileName);

};

const handleExportRastreoPDF = async () => {
  const rowsToExport = getRowsToExport();

  if (!rowsToExport.length) {
    toast.info("Selecciona al menos una chapa", { toastId: "no-rows-export" });
    return;
  }

  // Cargar logo
  let logoDataUrl = null;
  try {
    const imgResp = await fetch(`${process.env.PUBLIC_URL}/assets/dracena.png`);
    const blob = await imgResp.blob();
    logoDataUrl = await new Promise((res) => {
      const reader = new FileReader();
      reader.onloadend = () => res(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("No se pudo cargar el logo:", e);
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  // ── Dimensiones base ──────────────────────────────────────────────────
  const ML   = 10;   // margen izquierdo
  const W    = 190;  // ancho útil

  // Header
  const logoW  = 35;
  const titleW = 100;
  const regW   = 55;  // bloque REGISTRO (logoW + titleW + regW = 190 ✓)
  const hdrSubH = 19; // altura de cada sub-fila del bloque título
  const hdrH    = hdrSubH * 2; // 38mm altura total header
  const regHdrH = 9;  // fila "REGISTRO DE LLEGADA DE CAMIÓN"
  const regRowH = (hdrH - regHdrH) / 4; // 4 filas de datos (~7.25mm)

  // Secciones (4 columnas iguales)
  const cW  = W / 4; // 47.5mm por sección
  const cX  = [ML, ML + cW, ML + cW * 2, ML + cW * 3];

  // Dentro de cada sección: sub-col label | sub-col valor
  const lblW = 26;
  const valW = cW - lblW; // ~21.5mm

  // Filas de datos
  const secHdrH = 7;  // altura cabecera de sección
  const rH      = 8;  // altura fila de datos

  // Helper: texto dentro de celda con truncado seguro
  const cellText = (text, x, y, maxW, fontSize, bold) => {
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", bold ? "bold" : "normal");
    const str = String(text ?? "");
    const lines = doc.splitTextToSize(str, maxW - 1);
    doc.text(lines[0] ?? "", x + 1.5, y + rH / 2 + 1.5);
  };

  rowsToExport.forEach((row, idx) => {
    if (idx > 0) doc.addPage();

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    let y = 10;

    // ─── ENCABEZADO ───────────────────────────────────────────────────

    // Logo
    doc.rect(ML, y, logoW, hdrH);
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, "PNG", ML + 1, y + 2, logoW - 2, 20);
    }
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(10, 80, 10);
    // doc.text("GRUPODRACENAS.A", ML + logoW / 2, y + hdrH - 3, { align: "center" });
    doc.setTextColor(0);

    // Título superior
    doc.rect(ML + logoW, y, titleW, hdrSubH);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(
      "DATOS DE VIDRIOS LAMINADOS PARA LABORATORIO.",
      ML + logoW + titleW / 2, y + hdrSubH / 2 + 2, { align: "center" }
    );

    // Subtítulo inferior
    doc.rect(ML + logoW, y + hdrSubH, titleW, hdrSubH);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(
      "UNIDAD INDUSTRIAL DE VIDRIOS LAMINADOS",
      ML + logoW + titleW / 2, y + hdrSubH + hdrSubH / 2 + 2, { align: "center" }
    );

    // Bloque REGISTRO DE LLEGADA DE CAMIÓN
    const rx = ML + logoW + titleW;

    // Cabecera del bloque (fondo azul claro)
    doc.setFillColor(180, 210, 240);
    doc.rect(rx, y, regW, regHdrH, "FD");
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0);
    doc.text("REGISTRO DE LLEGADA DE CAMIÓN", rx + regW / 2, y + regHdrH - 2, { align: "center" });

    // 4 filas de datos del bloque REGISTRO
    const regFields = [
      { label: "Chapa de camión:",                  value: "" },
      { label: "N° de paquete:",                    value: String(row.fabrica1 ?? "") + " / " + String(row.fabrica2 ?? "") },
      { label: "Fecha de descarga en la empresa:",  value: formatDate(row.HoraSalaLimpia) || "" },
      { label: "Fecha de prueba en laboratorio:",   value: formatDate(row.HoraCalandra)   || "" },
    ];
    regFields.forEach((field, fi) => {
      const ry = y + regHdrH + fi * regRowH;
      doc.rect(rx, ry, regW, regRowH);
      doc.setFontSize(6.5);
      doc.setFont("helvetica", "bold");
      doc.text(field.label, rx + 1.5, ry + regRowH / 2 + 1.5);
      doc.setFont("helvetica", "normal");
      // valor a la derecha del label (estimamos ~30mm para el label)
      doc.text(String(field.value), rx + 31, ry + regRowH / 2 + 1.5);
    });

    // Número de página (esquina sup. derecha, fuera del bloque REGISTRO)
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Pág. ${idx + 1} / ${rowsToExport.length}`,
      ML + W, y - 2, { align: "right" }
    );

    y += hdrH + 1;

    // ─── CABECERAS DE SECCIÓN ─────────────────────────────────────────
    const sectionLabels = ["Cargadora", "Sala Limpia", "Horno", "Autoclave"];
    cX.forEach((cx, i) => {
      doc.setFillColor(210, 230, 210);
      doc.rect(cx, y, cW, secHdrH, "FD");
      doc.setFontSize(8.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      doc.text(sectionLabels[i], cx + cW / 2, y + secHdrH - 1.5, { align: "center" });
    });
    y += secHdrH;

    // ─── FILAS DE DATOS (label | valor, una sub-celda por cada) ───────
    const cargadoraRows = [
      { label: "FECHA Y HORA:", value: formatDate(row.HoraSalaLimpia) || "" },
      { label: "O.P",           value: String(row.orden    ?? "") },
      { label: "Serie 1:",      value: String(row.fabrica1 ?? "") },
      { label: "Serie 2:",      value: String(row.fabrica2 ?? "") },
      { label: "",              value: "" },
      { label: "",              value: "" },
    ];
    const salaLimpiaRows = [
      { label: "Temperatura:",  value: String(row.TemperaturaSalaLimpia ?? "") },
      { label: "Humedad:",      value: String(row.HumedadSalaLimpia     ?? "") },
      { label: "Cod. De PVB:",  value: String(row.cod                   ?? "") },
      { label: "PVB:",          value: String(row.PVB                   ?? "") },
      { label: "",              value: "" },
      { label: "",              value: "" },
    ];
    const hornoRows = [
      { label: "Configuración:",      value: "" },
      { label: "1ra Calandra:",       value: String(row.nfChapa1    ?? "") },
      { label: "2da Calandra:",       value: String(row.nfChapa2    ?? "") },
      { label: "Velocidad:",          value: "" },
      { label: "Temp. de Chapa:",     value: String(row.temperatura ?? "") },
      { label: "N° de Chapa:",        value: String(row.id          ?? "") },
    ];
    const autoclaveRows = [
      { label: "Receta Utilizada:",   value: String(row.recetaAutoClave ?? "") },
      { label: "Fecha de Proceso:",   value: formatDate(row.HoraCalandra) || "" },
      { label: "Horario de inicio:",  value: "" },
      { label: "Horario final:",      value: "" },
      { label: "",                    value: "" },
      { label: "",                    value: "" },
    ];

    const allCols = [cargadoraRows, salaLimpiaRows, hornoRows, autoclaveRows];
    const maxR = Math.max(...allCols.map((c) => c.length));

    for (let r = 0; r < maxR; r++) {
      cX.forEach((cx, ci) => {
        // sub-celda label
        doc.rect(cx, y, lblW, rH);
        // sub-celda valor
        doc.rect(cx + lblW, y, valW, rH);

        const item = allCols[ci][r];
        if (item?.label) {
          cellText(item.label, cx,        y, lblW, 7, true);
          cellText(item.value, cx + lblW, y, valW, 7, false);
        }
      });
      y += rH;
    }

    // ─── PIE ──────────────────────────────────────────────────────────
    y += 3;
    doc.rect(ML,      y, 38,      8);
    doc.rect(ML + 38, y, W - 38, 8);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text("Responsable:", ML + 1.5, y + 5.5);
  });

  const fileName =
    rowsToExport.length === 1
      ? `RastreoChapa_${rowsToExport[0].serie ?? rowsToExport[0].id}.pdf`
      : `RastreoChapas_${dayjs().format("YYYYMMDD_HHmm")}.pdf`;

  doc.save(fileName);
};


  // Definir columnas de la tabla
  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "orden", headerName: "Orden", width: 70 },
    {
      field: "id_produccion",
      headerName: "ID Producción",
      width: 100,
      cellClassName: "name-column--cell",
    },
    {
      field: "serie",
      headerName: "Serie",
      width: 150,
      type: "string",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "cod",
      headerName: "Producto",
      width: 350,
      headerAlign: "left",
      align: "left",
      cellClassName: "name-column--cell",
    },
    {
      field: "fabrica1",
      headerName: "Chapa1",
      width: 150,
    },
    {
      field: "nfChapa1",
      headerName: "NF1",
      width: 150,
    },
    {
      field: "fabrica2",
      headerName: "Chapa2",
      width: 150,
    },
    {
      field: "nfChapa2",
      headerName: "NF2",
      width: 150,
    },
    {
      field: "PVB",
      headerName: "PVB",
      width: 150,
    },
   
    {
      field: "TemperaturaSalaLimpia",
      headerName: "SL Temperatura",
      width: 150,
      cellClassName: "name-column--cell",
    },
    {
      field: "HumedadSalaLimpia",
      headerName: "SL Humedad",
      width: 150,
      type: "number",
      headerAlign: "left",
      align: "left",
      cellClassName: "name-column--cell",
    },
    {
      field: "Hora SalaLimpia",
      headerName: "Hora SL",
      width: 150,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => formatDate(params.row.HoraSalaLimpia),
    },
    {
      field: "temperatura",
      headerName: "Calandra Temp",
      width: 150,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "clasificacion",
      headerName: "Clasificación",
      width: 150,
      headerAlign: "center",
      align: "center",
      cellClassName: "name-column--cell",
    },
    {
      field: "defectoCalandra",
      headerName: "Defecto Calandra",
      width: 150,
    },
    {
      field: "Procesado",
      headerName: "Hora Proceso",
      width: 150,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => formatDate(params.row.HoraCalandra),
    },
    {
      field: "recetaAutoClave",
      headerName: "Receta",
      width: 250,
    },
    {
      field: "HoraInterfoliacion",
      headerName: "Hora Interfoliación",
      width: 150,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => formatDate(params.row.HoraInterfoliacion),
    },
    {
      field: "tipoInterfoliacion",
      headerName: "Tipo Interfoliación",
      width: 150,
      headerAlign: "center",
      align: "center",
      cellClassName: "name-column--cell",
    },
    {
      field: "defecto",
      headerName: "Defecto Interfoliación",
      width: 150,
    },
    {
      field: "ColarInterfoliacion",
      headerName: "Colar Interfoliación",
      width: 150,
    },
  ];


  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchValue(value);
  };

  const handleClearFilters = () => {
    setStartDate(getDefaultStart());
    setEndDate(getDefaultEnd());
    setSearchValue("");
    setClassificationFilter([]);
  };

  const handleClassificationChange = (option) => {
    setClassificationFilter((prev) => {
      if (prev.includes(option)) {
        return prev.filter((item) => item !== option);
      }
      return [...prev, option];
    });
  };

  // Función para asignar una clase a cada fila dependiendo del Estado
  const getRowClassName = (params) => {
    const estado = params.row.Estado;
    switch (estado) {
      case "programado":
        return "estado-pendiente";
      case "concluido":
        return "estado-completo";
      case "en proceso":
        return "estado-en-proceso";
      default:
        return "estado-default";
    }
  };

  return (
    <Box m="20px">
      {/* Fila 1: Header izquierda | Botones de exportación derecha */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header title="Historial de Chapas" subtitle="Numero de Serie" />
        <Box display="flex" gap={2}>
          <Button
            sx={{ background: "#143f04", width: "200px", height: "3rem", color: "white", borderRadius: "8px", "&:hover": { background: "#36a60f" } }}
            onClick={handleExportExcel}
            variant="contained"
          >
            <img src="https://cdn-icons-png.flaticon.com/512/732/732220.png" alt="Excel Icon" style={{ width: 24, height: 24, marginRight: 8 }} />
            Exportar Excel
          </Button>
          <Button
            sx={{ background: "#8a4b00", width: "220px", height: "3rem", color: "white", borderRadius: "8px", "&:hover": { background: "#bc6900" } }}
            onClick={handleExportRastreoPDF}
            variant="contained"
            startIcon={<AnalyticsIcon />}
          >
            Exportar Rastreo
          </Button>
          <Button
            sx={{ background: "#1f1f1f", width: "200px", height: "3rem", color: "white", borderRadius: "8px", "&:hover": { background: "#494949" } }}
            onClick={handleExportWord}
            variant="contained"
          >
            <img src="https://cdn-icons-png.flaticon.com/512/281/281760.png" alt="Word Icon" style={{ width: 24, height: 24, marginRight: 8 }} />
            Exportar Word
          </Button>
        </Box>
      </Box>

      {/* Fila 2: Filtros de fecha, clasificación y búsqueda */}
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={1}>
        <TextField
          label="Desde"
          type="datetime-local"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 210 }}
        />
        <TextField
          label="Hasta"
          type="datetime-local"
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 210 }}
        />
        <Box display="flex" gap={1} flexWrap="wrap">
          {classificationOptions.map((option) => {
            const isActive = classificationFilter.includes(option);
            return (
              <Button
                key={option}
                variant="outlined"
                onClick={() => handleClassificationChange(option)}
                sx={{
                  borderRadius: "10px",
                  padding: "8px 20px",
                  fontSize: "0.9rem",
                  backgroundColor: isActive ? "rgb(206, 220, 0)" : "transparent",
                  color: isActive ? "black" : colors.grey[100],
                  borderColor: colors.blueAccent[300],
                  minWidth: "unset",
                  "&:hover": { backgroundColor: "rgb(206, 220, 0)", color: "black" },
                }}
              >
                {option}
              </Button>
            );
          })}
        </Box>
        <Button variant="outlined" color="inherit" onClick={handleClearFilters} sx={{ height: "40px" }}>
          Limpiar filtros
        </Button>
        <Box display="flex" backgroundColor={colors.primary[400]} borderRadius="3px" flex={1} minWidth={200}>
          <InputBase sx={{ ml: 2, flex: 1 }} placeholder="Buscar" value={searchValue} onChange={handleSearchChange} />
          <IconButton type="button" sx={{ p: 1 }}>
            <SearchIcon />
          </IconButton>
        </Box>
      </Box>
      <Box
        mt={2}
        height="68vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },

          // Estilos para las filas con hover
          "& .estado-pendiente": {
            backgroundColor: "#740017", // Color de texto
            textShadow: "2px 2px 2px 2px #ffffff",
            "&:hover": {
              backgroundColor: "rgb(27 33 54) !important", // Fondo al pasar el ratón
            },
          },
          "& .estado-completo": {
            backgroundColor: "green",
            textShadow: "2px 2px 2px 2px #ffffff",
            "&:hover": {
              backgroundColor: "rgb(27 33 54) !important", // Fondo al pasar el ratón
            },
          },
          "& .estado-en-proceso": {
            backgroundColor: "#ff6105",
            textShadow: "2px 2px 2px 2px #ffffff",
            color: "white !important",
            "&:hover": {
              backgroundColor: "rgb(27 33 54) !important", // Fondo al pasar el ratón
            },
          },
          "& .estado-default": {
            color: "gray",
            textShadow: "2px 2px 2px 2px #ffffff",
            "&:hover": {
              backgroundColor: "rgb(27 33 54) !important", // Fondo al pasar el ratón
            },
          },
        }}
      >
          <DataGrid
            checkboxSelection
            disableRowSelectionOnClick
            rows={flattenFilteredRows()} // Aplanar los grupos para que se muestren correctamente en la tabla
          getRowId={(row) => row.id} // Acceder correctamente al campo `Id`
          columns={columns}
          onRowDoubleClick={handleRowClick}
          getRowClassName={getRowClassName} // Aplicar la clase condicional
          components={{ Toolbar: GridToolbar }}
            selectionModel={selectionModel}
            onSelectionModelChange={(newSelection) =>
              setSelectionModel(Array.isArray(newSelection) ? newSelection : [newSelection])
            }
        />
      </Box>
    </Box>
  );
};

export default HistorialChapa;
